import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ChangePasswordSchema } from '@/lib/schemas/auth';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';
import { PASSWORD_MIN_LENGTH } from '@/lib/password-policy';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { CosmoBudLogo } from '@/components/CosmoBudLogo';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

function errorMessage(code: string): string {
  switch (code) {
    case 'INVALID_CURRENT_PASSWORD':
      return 'Current password is incorrect.';
    case 'USER_NOT_FOUND':
      return 'Your session is no longer valid. Please sign in again.';
    default:
      return 'Could not change password. Please try again.';
  }
}

export function ChangePasswordPage() {
  const { changePassword, user, status } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [policyError, setPolicyError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorText(null);
    setPolicyError(null);

    if (newPassword !== confirm) {
      setPolicyError('New password and confirmation do not match.');
      return;
    }

    // For first-time password change (must_change), only validate new password
    if (status === 'must_change') {
      if (!newPassword || newPassword.length < PASSWORD_MIN_LENGTH) {
        setPolicyError(`New password must be at least ${PASSWORD_MIN_LENGTH} characters`);
        return;
      }
      if (newPassword.length > 256) {
        setPolicyError('New password must be at most 256 characters');
        return;
      }
    } else {
      // For regular password changes, validate both current and new passwords
      const parsed = ChangePasswordSchema.safeParse({ currentPassword, newPassword });
      if (!parsed.success) {
        const first = parsed.error.issues[0];
        setPolicyError(first?.message ?? 'Invalid input.');
        return;
      }
    }

    setBusy(true);
    try {
      // For first-time password change, use empty string for current password
      const current = status === 'must_change' ? '' : currentPassword;
      await changePassword(current, newPassword);
      setSuccess(true);
      // Backend revoked refresh; AuthContext drops to guest. Send to login.
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (err) {
      if (err instanceof ApiError) {
        // Session expired or invalid - redirect to login to re-authenticate
        if (err.status === 401) {
          navigate('/login', { replace: true });
        } else {
          setErrorText(errorMessage(err.errorCode));
        }
      } else {
        setErrorText('Network error. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  const heading =
    status === 'must_change'
      ? 'Set your real password'
      : 'Change password';
  const description =
    status === 'must_change'
      ? 'You signed in with a temporary password. Set a new one to continue.'
      : `Update the password on ${user?.email ?? 'your account'}.`;

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <CosmoBudLogo size="md" showText={true} textLayout="horizontal" />
          </div>
          <CardTitle>{heading}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          {success && (
            <Alert variant="success" className="mb-4">
              <AlertTitle>Password updated</AlertTitle>
              <AlertDescription>
                Your password has been changed successfully. A confirmation email has been sent. Sign in again with your new password — redirecting…
              </AlertDescription>
            </Alert>
          )}
          {errorText && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Could not change password</AlertTitle>
              <AlertDescription>{errorText}</AlertDescription>
            </Alert>
          )}
          {policyError && (
            <Alert variant="warning" className="mb-4">
              <AlertDescription>{policyError}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            {status !== 'must_change' && (
              <div className="space-y-1.5">
                <Label htmlFor="current">Current password</Label>
                <PasswordInput
                  id="current"
                  autoComplete="current-password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrent(e.target.value)}
                  disabled={busy || success}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="new">
                New password
                <span className="ml-2 text-xs font-normal text-slate-500">
                  (≥ {PASSWORD_MIN_LENGTH} chars, must differ)
                </span>
              </Label>
              <PasswordInput
                id="new"
                autoComplete="new-password"
                required
                value={newPassword}
                onChange={(e) => setNew(e.target.value)}
                disabled={busy || success}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm">Confirm new password</Label>
              <PasswordInput
                id="confirm"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={busy || success}
              />
            </div>
            <Button type="submit" disabled={busy || success} className="w-full">
              {busy ? 'Updating…' : 'Update password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
